import type { HomeLimitUsage, WorkspaceHomeSummary } from "../../services/workspaceService";

export type AttentionSeverity = "error" | "warning" | "info";

export type AttentionItem = {
  key: string;
  severity: AttentionSeverity;
  /** Number to show next to the message; omitted for status-only items. */
  count?: number;
  to: string;
};

/** Usage at or above this share of a package cap is flagged on the home screen. */
export const QUOTA_WARN_RATIO = 0.8;

export function quotaRatio(item: HomeLimitUsage): number | null {
  if (item.max == null || item.max <= 0) return null;
  return item.used / item.max;
}

/** Turns the snapshot into the "needs attention" list, most severe first. Absent sections yield nothing. */
export function deriveAttentionItems(summary: WorkspaceHomeSummary | null | undefined): AttentionItem[] {
  if (!summary) return [];
  const items: AttentionItem[] = [];
  const { health, anomalies, dataPipeline, schedules, limits } = summary;

  if (health?.setupComplete === false) {
    items.push({ key: "setupIncomplete", severity: "error", to: "/workspace-settings" });
  } else {
    if (health?.llmOk === false) items.push({ key: "llmDown", severity: "error", to: "/workspace-settings" });
    if (health?.openSearchOk === false) {
      items.push({ key: "openSearchDown", severity: "error", to: "/workspace-settings" });
    }
  }
  if (anomalies && anomalies.openIncidents > 0) {
    items.push({ key: "openIncidents", severity: "error", count: anomalies.openIncidents, to: "/anomalies?tab=incidents" });
  }
  if (dataPipeline && dataPipeline.ingestFailed7d > 0) {
    items.push({ key: "ingestFailed", severity: "warning", count: dataPipeline.ingestFailed7d, to: "/data-sources?tab=ingest" });
  }
  if (dataPipeline && dataPipeline.prepFailedRuns7d > 0) {
    items.push({ key: "prepFailed", severity: "warning", count: dataPipeline.prepFailedRuns7d, to: "/data-prep" });
  }
  if (schedules && schedules.failing > 0) {
    items.push({ key: "schedulesFailing", severity: "warning", count: schedules.failing, to: "/insight-report-jobs" });
  }
  if (dataPipeline?.pendingIndexApprovals && dataPipeline.pendingIndexApprovals > 0) {
    items.push({
      key: "pendingApprovals",
      severity: "info",
      count: dataPipeline.pendingIndexApprovals,
      to: "/data-prep?tab=approvals",
    });
  }
  if (health && health.businessErrors24h > 0) {
    items.push({ key: "businessErrors", severity: "info", count: health.businessErrors24h, to: "/logs?tab=errors" });
  }
  const nearLimit = (limits?.items ?? []).filter((i) => {
    const ratio = quotaRatio(i);
    return ratio != null && ratio >= QUOTA_WARN_RATIO;
  });
  if (nearLimit.length > 0) {
    items.push({ key: "quotaNearLimit", severity: "warning", count: nearLimit.length, to: "/workspace-settings" });
  }

  const rank: Record<AttentionSeverity, number> = { error: 0, warning: 1, info: 2 };
  return items.sort((a, b) => rank[a.severity] - rank[b.severity]);
}

/** Time-of-day greeting bucket for the welcome header. */
export function greetingKey(date: Date = new Date()): "morning" | "afternoon" | "evening" {
  const h = date.getHours();
  if (h < 12) return "morning";
  if (h < 18) return "afternoon";
  return "evening";
}
